import Box from "@material-ui/core/Box";
import Button from "@material-ui/core/Button";
import React, { FC } from "react";
import {
  Datagrid,
  FunctionField,
  List,
  ListProps,
  ReferenceField,
  TextField,
} from "react-admin";
import BookingCalendar from "../../components/BookingCalendar";
import BookingKanban from "../../components/BookingKanban";
import { BookingIdField, HospitalRoomField } from "../../components/fields";
import { formatDisplayDateTime } from "../../utils/date";
import BookingFilter from "./BookingFilter";
import BookingBulkActions from "./actions/BookingBulkActions";

// Define which statuses can be verified
const VERIFIABLE_STATUSES = ["allocated", "delivered", "report_submitted"];
const isBookingVerifiable = (status: string) =>
  VERIFIABLE_STATUSES.includes(status);

type BookingViewMode = "list" | "calendar" | "kanban";

export const BookingList: FC<ListProps> = (props) => {
  const [viewMode, setViewMode] = React.useState<BookingViewMode>("list");
  const isListView = viewMode === "list";

  React.useEffect(() => {
    const cls = "bookings-calendar-open";
    if (isListView) {
      document.body.classList.remove(cls);
    } else {
      document.body.classList.add(cls);
    }
    return () => document.body.classList.remove(cls);
  }, [isListView]);

  return (
    <>
      <Box width="100%" textAlign="left">
        <Button
          className="booking-view-switch"
          variant="text"
          size="small"
          onClick={() => setViewMode(isListView ? "calendar" : "list")}
        >{`Switch to ${isListView ? "Calendar" : "List"} View`}</Button>
        {!isListView && (
          <Button
            className="booking-view-switch"
            variant="text"
            size="small"
            onClick={() =>
              setViewMode(viewMode === "kanban" ? "calendar" : "kanban")
            }
          >{`Use ${viewMode === "kanban" ? "Calendar" : "Kanban"} View`}</Button>
        )}
      </Box>

      {viewMode === "calendar" && (
        <Box
          className="bookings-calendar-view"
          style={{ minWidth: 0, maxWidth: "100%" }}
        >
          <BookingCalendar />
        </Box>
      )}

      {viewMode === "kanban" && (
        <Box
          className="bookings-calendar-view"
          style={{ minWidth: 0, maxWidth: "100%" }}
        >
          <BookingKanban />
        </Box>
      )}

      {isListView && (
        <List
          {...props}
          sort={{ field: "id", order: "DESC" }}
          exporter={false}
          bulkActionButtons={<BookingBulkActions />}
          filters={<BookingFilter />}
        >
          <Datagrid
            isRowSelectable={(record) => isBookingVerifiable(record?.status)}
          >
            <BookingIdField />
            <FunctionField
              label="Date"
              sortBy="date"
              render={(record: any) =>
                record?.date ? formatDisplayDateTime(record.date) : ""
              }
            />

            <ReferenceField source="consultant_id" reference="consultants">
              <TextField source="full_name" />
            </ReferenceField>
            <HospitalRoomField
              source="hospital_room.name"
              hospitalSource="hospital.name"
            />
            <ReferenceField
              source="procedure_id"
              reference="procedures"
              label={
                process.env.REACT_APP_COMPANY_CODE === "STMY"
                  ? "Order Type"
                  : "Procedure"
              }
            >
              <TextField source="name" />
            </ReferenceField>
            <TextField source="surgeon.full_name" label="Surgeon" />
            <TextField source="business_unit.name" label="Business Unit" />
          </Datagrid>
        </List>
      )}
    </>
  );
};
